"use client";

import { AlertTriangle, Clock, ShieldX, Shield } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import type { A2PStatus } from "@/app/_module/services/a2pService";

export type A2PStage = "verified" | "pending" | "rejected" | "required";

/** Title used across every A2P gate surface — keep them identical. */
export const A2P_STAGE_TITLE: Record<A2PStage, string> = {
  verified: "A2P Verified",
  pending: "A2P Verification Pending",
  rejected: "A2P Verification Rejected",
  required: "A2P Verification Required",
};

/**
 * Single source of truth for the "you can't send SMS yet" notice, so every
 * surface (Send SMS modals, Sentimentals, ERS, widgets…) shows the SAME thing
 * the A2P tab does — a distinct PENDING state instead of always saying
 * "Verification Required" even when the user has submitted and is under review.
 */
export function a2pStageFromStatus(status?: A2PStatus | null): A2PStage {
  switch (status) {
    case "verified":
      return "verified";
    case "pending":
    case "pending_review":
    case "pending_payment":
      return "pending";
    case "rejected":
    case "expired":
      return "rejected";
    default:
      return "required";
  }
}

const CONFIG: Record<Exclude<A2PStage, "verified">, {
  title: string;
  icon: typeof Clock;
  action: string;
  wrap: string;
  title_cls: string;
  body_cls: string;
  icon_cls: string;
  defaultReason: string;
  primary: boolean;
}> = {
  pending: {
    title: "A2P Verification Pending",
    icon: Clock,
    action: "View A2P Status",
    wrap: "border-yellow-300 bg-yellow-50",
    title_cls: "text-yellow-900",
    body_cls: "text-yellow-800",
    icon_cls: "text-yellow-600",
    defaultReason:
      "Your A2P registration is under carrier review. SMS will unlock automatically once it’s approved (typically 1-4 business days).",
    primary: false,
  },
  rejected: {
    title: "A2P Verification Rejected",
    icon: ShieldX,
    action: "Fix A2P Verification",
    wrap: "border-red-300 bg-red-50",
    title_cls: "text-red-900",
    body_cls: "text-red-800",
    icon_cls: "text-red-600",
    defaultReason: "Your A2P verification was rejected. Please review the reason and resubmit.",
    primary: true,
  },
  required: {
    title: "A2P Verification Required",
    icon: AlertTriangle,
    action: "Complete A2P Verification",
    wrap: "border-red-300 bg-red-50",
    title_cls: "text-red-900",
    body_cls: "text-red-800",
    icon_cls: "text-red-600",
    defaultReason: "You must complete A2P verification before sending SMS messages.",
    primary: true,
  },
};

interface Props {
  stage: A2PStage;
  reason?: string;
  /** Fires the CTA (e.g. navigate to the A2P tab). Hidden when omitted. */
  onAction?: () => void;
  className?: string;
}

export function A2PGateNotice({ stage, reason, onAction, className }: Props) {
  if (stage === "verified") return null;
  const c = CONFIG[stage] ?? CONFIG.required;
  const Icon = c.icon;
  return (
    <Alert className={`${c.wrap} ${className ?? ""}`}>
      <Icon className={`h-4 w-4 ${c.icon_cls}`} />
      <AlertDescription className="space-y-3">
        <div>
          <strong className={c.title_cls}>{c.title}</strong>
          <p className={`text-sm mt-1 ${c.body_cls}`}>{reason || c.defaultReason}</p>
        </div>
        {onAction && (
          <Button
            onClick={onAction}
            size="sm"
            variant={c.primary ? "default" : "outline"}
            className={c.primary ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <Shield className="h-4 w-4 mr-2" />
            {c.action}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
